import React, { useEffect, useState } from "react";
import Layout from "../layouts/Layout";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import { toast } from "react-toastify";
import { useUserContext } from "../context/userContext";
import { getEnterprises } from "../services/api/bidderService";
import { OfferStatus } from "../helpers/bidderHelper";
import SentimentIconRating from "../components/ui/SentimentIconRating";
import { Button } from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

export default function Enterprises() {
  const { user } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [enterprises, setEnterprises] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const columns = [
    {
      field: "enterpriseNumber",
      headerName: "Numéro d'entreprise",
      flex: 0.3,
    },
    {
      field: "name",
      headerName: "Nom",
      flex: 1,
      renderCell: params => (
        <span
          className="flex w-full text-sky-500 cursor-pointer"
          onMouseDown={event => handleClick(event, params.row.uuid)}
        >
          <span className="underline decoration-sky-500 truncate">{params.value}</span>
        </span>
      ),
    },
    {
      field: "legalForm",
      headerName: "Forme légale",
      renderCell: (params) => {
        const fullData = params.row.fullData;
        const legalForm = params.row.legalForm;
        if (fullData && fullData.identification?.legalForm?.labelAbbreviated) {
          return fullData.identification.legalForm.labelAbbreviated;
        } else {
          return legalForm;
        }
      },
    },
    {
      field: "locality",
      headerName: "Localité",
    },
    {
      field: "country",
      headerName: "Pays",
    },
    {
      field: "contacts",
      headerName: "Nombre de sollicitations",
      valueGetter: (contacts) => {
        const procurements = new Set();
        contacts.forEach(contact => contact.procurements.forEach(procuement => procurements.add(procuement.uuid)));
        return procurements.size;
      },
    },
    {
      field: "offers",
      headerName: "Nombre d'offres déposées",
      valueGetter: offers => offers.length,
    },
    {
      field: "selectedOffers",
      headerName: "Nombre d'offres remportées",
      valueGetter: selectedOffers => selectedOffers.length,
    },
    {
      field: "reviews",
      headerName: "Notes",
      valueGetter: reviews => reviews.length,
    },
    {
      field: "rating",
      headerName: "Appréciation",
      renderCell: (params) => {
        const reviews = params.row.reviews;
        const avgRating = reviews.length > 0 ? Math.round(reviews.reduce((a, b) => a + b.rating.ratingValue, 0) / reviews.length) : 5;
        return <SentimentIconRating rating={avgRating} />;
      },
    },
  ];

  useEffect(() => {
    async function fetch() {
      try {
        setIsLoading(true);
        const result = await getEnterprises();
        if (result && Array.isArray(result)) {
          const rows = result.map((enterprise) => {
            return {
              ...enterprise,
              selectedOffers: enterprise.offers.filter(offer => offer.status === OfferStatus.SELECTED),
            };
          });
          setEnterprises(rows);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    }
    fetch();
  }, [user]);

  function handleClick(event, uuid) {
    const url = `/enterprise/${uuid}`;
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  }

  return (
    <Layout>
      <div className="flex flex-col mx-6 2xl:mx-72 py-10 min-w-3.5 space-y-4">
        <h1 className="text-4xl font-semibold self-center text-center">Liste des entreprises</h1>
        <div className="flex flex-row-reverse">
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => navigate("/")}
            startIcon={<ArrowBackIosNewIcon />}
          >
            Retour
          </Button>
        </div>
        <DataGrid
          rows={enterprises}
          getRowId={enterprise => enterprise.uuid}
          columns={columns}
          getRowClassName={params => params.row.isUserAction ? "bg-blue-50" : ""}
          loading={isLoading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          paginationModel={paginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          onPaginationModelChange={model => setPaginationModel(model)}
          localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
        />
      </div>
    </Layout>
  );
}
